/* src/render/cartMarker.ts — the player's cart in the world (TASKS.md 2.10).
 *
 * The cart is the one object in this city the player owns, and for most of the early game it is
 * the *only* thing they own, so it earns real modelling: a wheeled chassis, a stainless counter,
 * a griddle under an extractor hood, a striped awning with a scalloped valance, a chalkboard,
 * condiments, a crate of stock. All procedural — no asset pipeline, no glTF, nothing to load.
 *
 * ── Why it is three meshes and not sixty ──────────────────────────────────────────────
 * Every `Mesh` is a draw call, and the budget is 300 for the whole street (CLAUDE.md). So the
 * parts are baked: each primitive gets its colour written into a vertex-colour attribute, is
 * transformed into cart space, and the lot is merged into a single geometry drawn with one
 * material. The awning stripes are a second mesh purely so their colour can follow the cuisine
 * without rebuilding anything, and the locator pin is a third because it animates and glows.
 *
 * Position arrives as lon/lat and has to survive the floating-origin rebase, so the marker is
 * re-placed from its geographic coordinates whenever the origin moves — exactly as tiles are
 * (see world/worldView.ts). Storing a local position and translating it would accumulate the
 * very drift the rebase exists to prevent.
 */

import {
  BoxGeometry,
  BufferAttribute,
  Color,
  CylinderGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  type BufferGeometry,
  type Matrix4,
  type Scene,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { toLocal, type Origin } from '../map/originRebase.ts';

export interface CartMarker {
  readonly object: Object3D;
  /** Re-place from lon/lat against the current origin. Call on placement and on rebase. */
  place: (lon: number, lat: number, origin: Origin) => void;
  /** Paint the awning in the cuisine's colour. Null while the menu is unchosen. */
  setCuisine: (cuisine: string | null) => void;
  setVisible: (visible: boolean) => void;
  dispose: () => void;
}

/* The palette. Warm and saturated: the cart has to be findable against a city of concrete. */
const CREAM = 0xf6ead2;
const CREAM_LIT = 0xfdf6e8;
const TIMBER = 0x9c6239;
const TIMBER_LIT = 0xb98455;
const STEEL = 0xc6ccd4;
const STEEL_DARK = 0x878e97;
const CHARCOAL = 0x2b2f36;
const SLATE = 0x474d55;
const CHALK = 0xe9e5d6;

/** Each cuisine gets its own awning, so a street of carts reads at a glance. */
const CUISINE_COLOURS: Readonly<Record<string, number>> = {
  burger: 0xd94f3d,
  hotdog: 0xe4572e,
  pizza: 0xc9412f,
  taco: 0xe8a33d,
  burrito: 0xd98324,
  sushi: 0x2f8f83,
  noodle: 0xb5462f,
  crepe: 0xd96b8a,
  fishchips: 0x3f7fbf,
};
const DEFAULT_ACCENT = 0xd94f3d;

/**
 * Bake a primitive into cart space: colour into a vertex attribute, position into the vertices.
 * `mergeGeometries` needs every input to carry the same attributes, which is why the colour is
 * written even for parts that could have shared a material.
 */
function bake(geo: BufferGeometry, colour: number, matrix: Matrix4): BufferGeometry {
  geo.applyMatrix4(matrix);
  const c = new Color(colour);
  const count = geo.getAttribute('position').count;
  const rgb = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    rgb[i * 3] = c.r;
    rgb[i * 3 + 1] = c.g;
    rgb[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new BufferAttribute(rgb, 3));
  return geo;
}

/** Scratch object used only to compose transforms; never added to the scene. */
const poser = new Object3D();
function at(x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): Matrix4 {
  poser.position.set(x, y, z);
  poser.rotation.set(rx, ry, rz);
  poser.updateMatrix();
  return poser.matrix.clone();
}

const HALF_PI = Math.PI / 2;

/*
 * The cart is modelled facing +Z — that is the counter and the serving side. X runs along its
 * length (the handle is at +X), Y is up from the ground it stands on.
 */
function buildCart(): { shell: BufferGeometry; stripes: BufferGeometry } {
  const shell: BufferGeometry[] = [];
  const stripes: BufferGeometry[] = [];

  const add = (
    geo: BufferGeometry,
    colour: number,
    x: number, y: number, z: number,
    rx = 0, ry = 0, rz = 0,
  ): void => {
    shell.push(bake(geo, colour, at(x, y, z, rx, ry, rz)));
  };

  // ── Running gear ────────────────────────────────────────────────────────────────────
  const WHEEL_R = 0.36;
  for (const x of [-1.05, 1.05]) {
    for (const z of [-0.68, 0.68]) {
      add(new CylinderGeometry(WHEEL_R, WHEEL_R, 0.13, 16), CHARCOAL, x, WHEEL_R, z, HALF_PI);
      // Hub outboard of the tyre, so it catches light from the side.
      add(new CylinderGeometry(0.12, 0.12, 0.16, 10), STEEL, x, WHEEL_R, z * 1.06, HALF_PI);
    }
    add(new CylinderGeometry(0.05, 0.05, 1.45, 8), SLATE, x, WHEEL_R, 0, HALF_PI);
  }

  // Under-frame the body sits on, set in from the body so the cart reads as *on* wheels.
  add(new BoxGeometry(2.9, 0.16, 1.36), SLATE, 0, 0.66, 0);

  // ── Body ────────────────────────────────────────────────────────────────────────────
  add(new BoxGeometry(3.0, 0.92, 1.5), CREAM, 0, 1.2, 0);
  // Horizontal timber trim, top and bottom — the line that makes it a market cart.
  add(new BoxGeometry(3.06, 0.07, 1.56), TIMBER, 0, 1.62, 0);
  add(new BoxGeometry(3.06, 0.07, 1.56), TIMBER, 0, 0.79, 0);
  for (const x of [-1.5, 1.5]) {
    for (const z of [-0.75, 0.75]) {
      add(new BoxGeometry(0.11, 0.94, 0.11), TIMBER, x, 1.2, z);
    }
  }
  // Livery panels front and back, in the cuisine's colour.
  for (const z of [0.78, -0.78]) {
    stripes.push(bake(new BoxGeometry(2.66, 0.46, 0.06), DEFAULT_ACCENT, at(0, 1.16, z)));
  }
  // A blank sign plate over the livery. Deliberately wordless — no branding (CLAUDE.md Legal).
  add(new BoxGeometry(1.15, 0.2, 0.04), CREAM_LIT, 0, 1.33, 0.83);

  // ── Counter ─────────────────────────────────────────────────────────────────────────
  add(new BoxGeometry(3.28, 0.09, 1.72), STEEL, 0, 1.71, 0);
  add(new BoxGeometry(3.28, 0.08, 0.07), TIMBER_LIT, 0, 1.63, 0.86);
  add(new BoxGeometry(3.28, 0.08, 0.07), TIMBER_LIT, 0, 1.63, -0.86);

  // ── Griddle, hood and flue ──────────────────────────────────────────────────────────
  add(new BoxGeometry(1.15, 0.18, 0.86), STEEL_DARK, -0.85, 1.84, 0);
  add(new BoxGeometry(1.0, 0.04, 0.72), CHARCOAL, -0.85, 1.95, 0);
  for (const x of [-1.36, -0.34]) {
    add(new BoxGeometry(0.06, 0.52, 0.06), STEEL_DARK, x, 2.22, -0.36);
  }
  add(new BoxGeometry(1.3, 0.2, 0.98), STEEL, -0.85, 2.56, -0.04);
  add(new CylinderGeometry(0.085, 0.085, 0.78, 10), STEEL_DARK, -1.28, 3.03, -0.04);
  add(new CylinderGeometry(0.135, 0.135, 0.07, 10), STEEL_DARK, -1.28, 3.44, -0.04);

  // ── Counter clutter — the detail that sells the scale ───────────────────────────────
  add(new CylinderGeometry(0.22, 0.25, 0.3, 12), STEEL, 0.2, 1.9, -0.25);
  add(new CylinderGeometry(0.25, 0.1, 0.09, 12), STEEL_DARK, 0.2, 2.09, -0.25);
  add(new CylinderGeometry(0.055, 0.055, 0.24, 8), 0xd94f3d, 0.98, 1.87, 0.3);
  add(new CylinderGeometry(0.03, 0.055, 0.08, 8), CHARCOAL, 0.98, 2.03, 0.3);
  add(new CylinderGeometry(0.055, 0.055, 0.24, 8), 0xf2c14e, 1.15, 1.87, 0.22);
  add(new CylinderGeometry(0.03, 0.055, 0.08, 8), CHARCOAL, 1.15, 2.03, 0.22);
  // A tray of whatever this cart sells, in its colour.
  stripes.push(bake(new BoxGeometry(0.52, 0.09, 0.36), DEFAULT_ACCENT, at(0.62, 1.8, 0.36)));
  add(new BoxGeometry(0.58, 0.03, 0.42), STEEL, 0.62, 1.76, 0.36);

  // ── Awning ──────────────────────────────────────────────────────────────────────────
  for (const x of [-1.5, 1.5]) {
    for (const z of [-0.78, 0.78]) {
      add(new CylinderGeometry(0.045, 0.045, 1.3, 8), STEEL_DARK, x, 2.4, z);
    }
    add(new BoxGeometry(0.06, 0.06, 1.6), STEEL_DARK, x, 3.03, 0);
  }

  const TILT = 0.18;                       // front edge dips, so rain and light both run off it
  const SLATS = 12;
  const SLAT_W = 3.48 / SLATS;
  for (let i = 0; i < SLATS; i += 1) {
    const x = -1.74 + SLAT_W * (i + 0.5);
    const slat = new BoxGeometry(SLAT_W * 0.98, 0.07, 2.2);
    const matrix = at(x, 3.16, 0.06, TILT);
    if (i % 2 === 0) stripes.push(bake(slat, DEFAULT_ACCENT, matrix));
    else shell.push(bake(slat, CREAM_LIT, matrix));

    // Scalloped valance hanging off the front edge, following the same tilt.
    const scallop = new BoxGeometry(SLAT_W * 0.9, 0.22, 0.06);
    const sm = at(x, 2.92, 1.14, TILT);
    if (i % 2 === 0) stripes.push(bake(scallop, DEFAULT_ACCENT, sm));
    else shell.push(bake(scallop, CREAM_LIT, sm));
  }
  add(new BoxGeometry(3.6, 0.1, 0.13), TIMBER, 0, 3.36, -1.02);

  // ── Chalkboard, hung off the awning rail ────────────────────────────────────────────
  add(new BoxGeometry(0.88, 0.62, 0.05), 0x23261f, 1.0, 2.5, 0.86);
  add(new BoxGeometry(0.94, 0.05, 0.07), TIMBER, 1.0, 2.83, 0.86);
  for (let i = 0; i < 3; i += 1) {
    add(new BoxGeometry(0.5 - i * 0.1, 0.035, 0.02), CHALK, 0.94, 2.66 - i * 0.14, 0.9);
  }
  for (const x of [0.7, 1.3]) {
    add(new CylinderGeometry(0.014, 0.014, 0.22, 6), STEEL_DARK, x, 2.92, 0.86);
  }

  // ── Push handle ─────────────────────────────────────────────────────────────────────
  for (const z of [-0.5, 0.5]) {
    add(new CylinderGeometry(0.038, 0.038, 0.46, 8), STEEL_DARK, 1.62, 1.42, z, 0, 0, 0.32);
  }
  add(new CylinderGeometry(0.038, 0.038, 1.05, 8), STEEL_DARK, 1.69, 1.63, 0, HALF_PI);

  // ── A crate of stock on the pavement beside it ──────────────────────────────────────
  add(new BoxGeometry(0.62, 0.5, 0.52), TIMBER, 1.42, 0.25, -1.16, 0, 0.22, 0);
  add(new BoxGeometry(0.66, 0.06, 0.56), TIMBER_LIT, 1.42, 0.53, -1.16, 0, 0.22, 0);
  add(new BoxGeometry(0.64, 0.06, 0.54), TIMBER_LIT, 1.42, 0.36, -1.16, 0, 0.22, 0);

  const merged = mergeGeometries(shell);
  const mergedStripes = mergeGeometries(stripes);
  if (!merged || !mergedStripes) {
    // mergeGeometries only returns null on mismatched attributes, which `bake` prevents.
    throw new Error('cartMarker: geometry merge failed');
  }
  merged.computeBoundingSphere();
  mergedStripes.computeBoundingSphere();
  return { shell: merged, stripes: mergedStripes };
}

export function createCartMarker(scene: Scene): CartMarker {
  const group = new Group();
  group.matrixAutoUpdate = false;

  const { shell, stripes } = buildCart();

  const shellMat = new MeshLambertMaterial({ vertexColors: true });
  const stripeMat = new MeshLambertMaterial({ color: DEFAULT_ACCENT });

  const shellMesh = new Mesh(shell, shellMat);
  shellMesh.castShadow = true;
  shellMesh.receiveShadow = true;

  const stripeMesh = new Mesh(stripes, stripeMat);
  stripeMesh.castShadow = true;

  /*
   * There is no floating locator pin. There was one — a cone hanging above roof height, on the
   * argument that it made the cart findable from across a block — and it read as a HUD element
   * dropped into the world, which is exactly what it was. The cart's own silhouette and its
   * cuisine-coloured awning do the finding now.
   */
  group.add(shellMesh, stripeMesh);
  scene.add(group);

  return {
    object: group,

    place(lon: number, lat: number, origin: Origin): void {
      const [x, z] = toLocal(lon, lat, origin);
      group.position.set(x, 0, z);
      group.updateMatrix();
    },

    setCuisine(cuisine: string | null): void {
      const colour = (cuisine !== null ? CUISINE_COLOURS[cuisine] : undefined) ?? DEFAULT_ACCENT;
      stripeMat.color.setHex(colour);
    },

    setVisible(visible: boolean): void {
      group.visible = visible;
    },

    dispose(): void {
      scene.remove(group);
      shell.dispose();
      stripes.dispose();
      shellMat.dispose();
      stripeMat.dispose();
    },
  };
}
